import { useCallback, useContext, useEffect, useRef } from "react";
import { DateInput } from "semantic-ui-calendar-react-yz";
import Moment from "moment";
import "moment/locale/en-gb";
import cn from "classnames";

import "./datePicker.scss";
import ModalContext from "components/modal/ModalContext";

export type PopupPosition =
  | "top left"
  | "top right"
  | "bottom left"
  | "bottom right"
  | "right center"
  | "left center"
  | "top center"
  | "bottom center";

interface DatePickerProps {
  placeholder?: string;
  value?: string;
  format?: string;
  iconPosition?: "left" | "right";
  closeOnMouseLeave?: boolean;
  onChangeDate?: (event: React.SyntheticEvent<HTMLElement>, data: any) => void;
  verticalPosition?: "top" | "bottom";
  horizontalPosition?: "auto" | "left" | "right";
  mountNode?: HTMLElement | null;
  minDate?: Moment.Moment | string;
  maxDate?: Moment.Moment | string;
  initialDate?: Moment.Moment | string;
  marked?: Moment.Moment[] | Date[];
  highlightExpired?: boolean;
  popupPosition?: PopupPosition;
  className?: string;
  disabled?: boolean;
  [key: string]: unknown;
}

const DatePicker = ({
  placeholder = "",
  value = "",
  iconPosition = "left",
  closeOnMouseLeave = true,
  mountNode,
  onChangeDate,
  verticalPosition = "bottom",
  horizontalPosition = "auto",
  minDate,
  maxDate,
  initialDate,
  marked,
  popupPosition = "bottom left",
  format,
  highlightExpired,
  className,
  ...additionalProps
}: DatePickerProps) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const isInModal = useContext(ModalContext);

  useEffect(() => {
    Moment.updateLocale("en-gb", {
      months: [
        "January,",
        "February,",
        "March,",
        "April,",
        "May,",
        "June,",
        "July,",
        "August,",
        "September,",
        "October,",
        "November,",
        "December,",
      ],
      weekdaysMin: ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"],
    });
  }, []);

  const markDate = (markedDates?: Moment.Moment[] | Date[], dateValue?: string) => {
    if (markedDates) return markedDates;
    if (dateValue) return undefined;
    return [new Date()];
  };

  const getMountNode = useCallback(() => {
    if (isInModal) {
      return document.body;
    }
    return mountNode || containerRef?.current;
  }, [isInModal, mountNode]);

  return (
    <div className={cn("date-picker", className)} data-testid="date-picker">
      <div
        className={cn("date-picker-container", verticalPosition, horizontalPosition)}
        ref={containerRef}
        data-testid="date-picker-container"
      />
      <DateInput
        autoComplete="off"
        marked={markDate(marked, value)}
        initialDate={initialDate}
        localization="en"
        dateFormat={format ?? "MM/DD/YYYY"}
        className={cn("input", { expired: highlightExpired })}
        closable
        name="date"
        placeholder={placeholder}
        value={value}
        minDate={minDate}
        maxDate={maxDate}
        iconPosition={iconPosition}
        closeOnMouseLeave={closeOnMouseLeave}
        mountNode={getMountNode()}
        onChange={onChangeDate || (() => {})}
        popupPosition={popupPosition}
        {...additionalProps}
      />
    </div>
  );
};

export default DatePicker;
